import { useEffect, useRef, useState } from 'react';
import { cameraScanSupported } from './camera';

interface DetectedBarcode {
  rawValue: string;
}

interface BarcodeDetectorLike {
  detect(source: HTMLVideoElement): Promise<DetectedBarcode[]>;
}

type BarcodeDetectorCtor = new (options: { formats: string[] }) => BarcodeDetectorLike;

/** Lector de QR con la cámara trasera usando `BarcodeDetector`. Si no se puede, avisa para pasar al ingreso manual. */
export function Scanner({ onDetected, onUnavailable }: { onDetected: (raw: string) => void; onUnavailable: (reason: 'unsupported' | 'denied' | 'error') => void }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [ready, setReady] = useState(false);
  const detectedRef = useRef(onDetected);
  const unavailableRef = useRef(onUnavailable);
  detectedRef.current = onDetected;
  unavailableRef.current = onUnavailable;

  useEffect(() => {
    if (!cameraScanSupported()) {
      unavailableRef.current('unsupported');
      return;
    }
    let stream: MediaStream | null = null;
    let timer: number | undefined;
    let stopped = false;
    const Detector = (window as unknown as { BarcodeDetector: BarcodeDetectorCtor }).BarcodeDetector;
    const detector = new Detector({ formats: ['qr_code'] });

    async function start() {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false });
      } catch (err) {
        if (!stopped) unavailableRef.current((err as { name?: string }).name === 'NotAllowedError' ? 'denied' : 'error');
        return;
      }
      const video = videoRef.current;
      if (!video || stopped) return;
      video.srcObject = stream;
      await video.play().catch(() => undefined);
      setReady(true);
      const tick = async () => {
        if (stopped) return;
        try {
          const codes = await detector.detect(video);
          if (codes[0]?.rawValue) {
            detectedRef.current(codes[0].rawValue);
            return;
          }
        } catch {
          /* cuadro sin QR o cámara ocupada: se reintenta */
        }
        timer = window.setTimeout(tick, 250);
      };
      void tick();
    }
    void start();
    return () => {
      stopped = true;
      if (timer) window.clearTimeout(timer);
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  return (
    <div className="relative aspect-square w-full max-w-full overflow-hidden rounded-lg bg-asphalt">
      <video ref={videoRef} className="h-full w-full object-cover" muted playsInline aria-label="Cámara para escanear el QR" />
      <div className="pointer-events-none absolute inset-8 rounded-lg border-4 border-yellow" aria-hidden="true" />
      <p className="absolute inset-x-0 bottom-3 text-center text-sm text-chalk" role="status">
        {ready ? 'Apunta la cámara al QR' : 'Abriendo la cámara…'}
      </p>
    </div>
  );
}
